import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { parseNavigationLinks } from '@/Components/AiChat/formatMessageContent';

const AiAssistantContext = createContext(null);

function getCsrfToken() {
    const match = document.cookie.match(/XSRF-TOKEN=([^;]+)/);

    return match ? decodeURIComponent(match[1]) : '';
}

function getAuthHeaders() {
    const token = localStorage.getItem('token');

    return {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        'X-XSRF-TOKEN': getCsrfToken(),
        'X-Requested-With': 'XMLHttpRequest',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
}

function conversationStorageKey(clubId) {
    return `ui.aiConversationId.${clubId}`;
}

function parseConfirmationFromOutput(output) {
    if (!output) {
        return null;
    }

    try {
        const parsed = typeof output === 'string' ? JSON.parse(output) : output;

        if (parsed?.status === 'confirmation_required') {
            return parsed;
        }
    } catch {
        return null;
    }

    return null;
}

export function AiAssistantProvider({ children }) {
    const [messages, setMessages] = useState([]);
    const [conversationId, setConversationId] = useState(null);
    const [isStreaming, setIsStreaming] = useState(false);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [pendingConfirmation, setPendingConfirmation] = useState(null);
    const [activeClubId, setActiveClubId] = useState(null);
    const [activeSquadId, setActiveSquadId] = useState(null);
    const abortRef = useRef(null);

    const loadConversation = useCallback(async (clubId) => {
        if (!clubId) {
            return;
        }

        setIsLoadingHistory(true);
        setActiveClubId(clubId);

        try {
            const response = await fetch(route('ai-assistant.conversation'), {
                headers: getAuthHeaders(),
                credentials: 'include',
            });

            if (!response.ok) {
                throw new Error('Failed to load conversation');
            }

            const data = await response.json();

            setConversationId(data.conversation_id);
            localStorage.setItem(conversationStorageKey(clubId), data.conversation_id);
            setMessages((data.messages ?? []).map((message) => ({
                ...message,
                links: parseNavigationLinks(message.content ?? ''),
            })));
            setPendingConfirmation(null);
        } catch {
            setMessages([]);
        } finally {
            setIsLoadingHistory(false);
        }
    }, []);

    useEffect(() => {
        return () => {
            abortRef.current?.abort();
        };
    }, []);

    const streamMessage = useCallback(async (message, options = {}) => {
        const trimmed = message.trim();

        if (!trimmed || isStreaming) {
            return;
        }

        const userMessage = {
            id: `user-${Date.now()}`,
            role: 'user',
            content: trimmed,
        };

        setMessages((current) => [...current, userMessage]);
        setIsStreaming(true);
        setPendingConfirmation(null);

        const assistantId = `assistant-${Date.now()}`;
        setMessages((current) => [...current, { id: assistantId, role: 'assistant', content: '', isStreaming: true }]);

        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        let full = '';
        let streamConversationId = options.conversationId ?? conversationId;

        try {
            const response = await fetch(route('ai-assistant.messages.stream'), {
                method: 'POST',
                headers: getAuthHeaders(),
                credentials: 'include',
                signal: controller.signal,
                body: JSON.stringify({
                    message: trimmed,
                    conversation_id: streamConversationId,
                    squad_id: options.squadId ?? activeSquadId ?? null,
                }),
            });

            if (!response.ok) {
                throw new Error(await response.text() || `HTTP ${response.status}`);
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();

            while (true) {
                const { done, value } = await reader.read();

                if (done) {
                    break;
                }

                const chunk = decoder.decode(value, { stream: true });
                const lines = chunk.split('\n');

                for (const line of lines) {
                    if (!line.startsWith('data: ')) {
                        continue;
                    }

                    const data = line.slice(6);

                    if (data === '[DONE]') {
                        continue;
                    }

                    try {
                        const parsed = JSON.parse(data);

                        if (parsed.conversation_id) {
                            streamConversationId = parsed.conversation_id;
                            setConversationId(parsed.conversation_id);

                            if (activeClubId) {
                                localStorage.setItem(conversationStorageKey(activeClubId), parsed.conversation_id);
                            }
                        }

                        if (parsed.type === 'text_delta' && parsed.delta) {
                            full += parsed.delta;
                        } else if (parsed.type === 'tool-output-available') {
                            const confirmation = parseConfirmationFromOutput(parsed.output);

                            if (confirmation) {
                                setPendingConfirmation(confirmation);
                            }
                        } else {
                            const content = parsed.choices?.[0]?.delta?.content
                                ?? parsed.content
                                ?? parsed.delta
                                ?? '';

                            if (content) {
                                full += content;
                            }
                        }
                    } catch {
                        full += data;
                    }

                    setMessages((current) => current.map((item) => (
                        item.id === assistantId
                            ? { ...item, content: full, links: parseNavigationLinks(full) }
                            : item
                    )));
                }
            }

            setMessages((current) => current.map((item) => (
                item.id === assistantId
                    ? { ...item, content: full, isStreaming: false, links: parseNavigationLinks(full) }
                    : item
            )));
        } catch (error) {
            if (error.name !== 'AbortError') {
                setMessages((current) => current.map((item) => (
                    item.id === assistantId
                        ? {
                            ...item,
                            content: 'Sorry, something went wrong. Please try again.',
                            isStreaming: false,
                        }
                        : item
                )));
            }
        } finally {
            setIsStreaming(false);
        }
    }, [activeClubId, activeSquadId, conversationId, isStreaming]);

    const confirmPendingAction = useCallback(() => {
        if (!pendingConfirmation) {
            return;
        }

        streamMessage('Yes, confirmed. Please proceed with the action.');
    }, [pendingConfirmation, streamMessage]);

    const dismissPendingConfirmation = useCallback(() => {
        setPendingConfirmation(null);
        streamMessage('No, cancel that action.');
    }, [streamMessage]);

    const value = useMemo(() => ({
        messages,
        conversationId,
        isStreaming,
        isLoadingHistory,
        pendingConfirmation,
        activeClubId,
        activeSquadId,
        setActiveSquadId,
        loadConversation,
        streamMessage,
        confirmPendingAction,
        dismissPendingConfirmation,
    }), [
        messages,
        conversationId,
        isStreaming,
        isLoadingHistory,
        pendingConfirmation,
        activeClubId,
        activeSquadId,
        loadConversation,
        streamMessage,
        confirmPendingAction,
        dismissPendingConfirmation,
    ]);

    return (
        <AiAssistantContext.Provider value={value}>
            {children}
        </AiAssistantContext.Provider>
    );
}

export function useAiAssistant() {
    const context = useContext(AiAssistantContext);

    if (!context) {
        throw new Error('useAiAssistant must be used within AiAssistantProvider');
    }

    return context;
}
